import { DashboardPanel } from '../DashboardPanel';
import React, { useEffect, useMemo, useState } from 'react';
import { OperationalCostPanelHeader } from './OperationalCostPanelHeader';
import { OperationalCostPanelBody } from './OperationalCostPanelBody';
import { OperationalCostPanelFooter } from './OperationalCostPanelFooter';
import {
  DASHBOARD_CURRENCY,
  resetTransactionsFilters,
  useDashboardFilteredTransactions,
} from '../../../../state/transactions/transactionFilterSlice';
import { useAppDispatch } from '../../../../state/hooks';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../../../../state/transactions/useTransactions';
import { ServiceType } from '../../../../codegen/vehicles';
import { serviceTypeColors } from '../../../../components/colors/ServiceTypeColors';
import { TRANSACTIONS_ROUTE } from '../../../../routes/routes';

type ChartData = {
  service: ServiceType;
  color: string;
  amount: number;
};

export const OperationalCostPanel: React.FC = () => {
  const { transactions, isLoading } = useTransactions();
  const { filteredTransactions, isLoading: isLoadingFilteredTransactions } =
    useDashboardFilteredTransactions();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [serviceFilter, setServiceFilter] = useState(
    new Map<ServiceType, boolean>([]),
  );

  useEffect(() => {
    if (!isLoading) {
      const services = (transactions || [])
        .map((t) => t.serviceType)
        .map((s) => [s, true] as [ServiceType, boolean]);

      setServiceFilter(new Map(services));
    }
  }, [transactions, isLoading]);

  const transactionsForChart = useMemo(() => {
    const enabledServices = Array.from(serviceFilter)
      .filter(([_, selected]) => selected)
      .map(([service]) => service);

    return filteredTransactions.filter((t) =>
      enabledServices.includes(t.serviceType),
    );
  }, [filteredTransactions, serviceFilter]);

  const totalCost = useMemo(() => {
    return transactionsForChart.reduce(
      (sum, t) => sum + parseFloat(t.dashboardAmount?.amountNet ?? '0'),
      0.0,
    );
  }, [transactionsForChart]);

  const filteredChartData = useMemo(() => {
    const costByServiceMap: { [key in ServiceType]: number } = {
      FUELING: 0,
      BREAKDOWN: 0,
      PARKING: 0,
      WASHING: 0,
      CHARGING: 0,
      OTHER: 0,
    };

    transactionsForChart.forEach((t) => {
      costByServiceMap[t.serviceType] += parseFloat(
        t.dashboardAmount?.amountNet ?? '0',
      );
    });

    const chartData: ChartData[] = [
      ServiceType.Fueling,
      ServiceType.Breakdown,
      ServiceType.Parking,
      ServiceType.Washing,
      ServiceType.Other,
      ServiceType.Charging,
    ].map((service: ServiceType) => ({
      service,
      color: serviceTypeColors[service].colorCssClass,
      amount: costByServiceMap[service],
    }));

    return chartData.filter((entry) => serviceFilter.get(entry.service));
  }, [transactionsForChart, serviceFilter]);

  const handleButtonClick = () => {
    dispatch(resetTransactionsFilters());
    navigate(TRANSACTIONS_ROUTE);
  };

  return (
    <DashboardPanel
      header={<OperationalCostPanelHeader />}
      testId="dashboard-operational-costs-panel"
      body={
        <OperationalCostPanelBody
          isLoading={isLoading || isLoadingFilteredTransactions}
          currency={DASHBOARD_CURRENCY}
          chartData={filteredChartData}
          totalAmount={totalCost}
        />
      }
      footer={
        <OperationalCostPanelFooter
          isLoading={isLoading || isLoadingFilteredTransactions}
          totalAmount={totalCost}
          currency={DASHBOARD_CURRENCY}
          onButtonClick={handleButtonClick}
        />
      }
    />
  );
};
